import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getLastYearTodayMemories, getRecentPhotos } from '@/services/photoService';
import type { PhotoDetail } from '@/types/photo';
import { getPopularAlbums, type AlbumData } from '@/services/albumService';
import {
  DEFAULT_HOME_SETTINGS,
  getHomeSettings,
  HOME_SETTINGS_CACHE_KEY,
  HOME_SETTINGS_EVENT,
  readCachedHomeSettings,
  type HomeSettings,
} from '@/services/siteSettingsService';

export default function HomePage() {
  const [recentPhotos, setRecentPhotos] = useState<PhotoDetail[]>([]);
  const [popularAlbums, setPopularAlbums] = useState<AlbumData[]>([]);
  const [memoryPhotos, setMemoryPhotos] = useState<PhotoDetail[]>([]);
  const [memoryDate, setMemoryDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [homeSettings, setHomeSettings] = useState<HomeSettings>(() => readCachedHomeSettings());

  useEffect(() => {
    let active = true;

    const refreshHomeSettings = async () => {
      try {
        const settings = await getHomeSettings();
        if (active) setHomeSettings(settings);
      } catch {
        if (active) setHomeSettings(readCachedHomeSettings());
      }
    };

    const loadPhotosAndAlbums = async () => {
      try {
        const [photos, albums, memories] = await Promise.all([
          getRecentPhotos(6),
          getPopularAlbums(2),
          getLastYearTodayMemories(),
        ]);
        if (!active) return;
        setRecentPhotos(photos);
        setPopularAlbums(albums);
        setMemoryPhotos(memories.photos);
        setMemoryDate(memories.memoryDate.replaceAll('-', '.'));
      } catch {
        // 사진이나 앨범 조회 오류가 홈 설정 표시를 막지 않게 분리합니다.
      } finally {
        if (active) setLoading(false);
      }
    };

    const handleRefresh = () => { void refreshHomeSettings(); };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refreshHomeSettings();
    };
    const handleSettingsUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<HomeSettings>;
      if (customEvent.detail) setHomeSettings(customEvent.detail);
      void refreshHomeSettings();
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === HOME_SETTINGS_CACHE_KEY) {
        setHomeSettings(readCachedHomeSettings());
      }
    };

    setHomeSettings(readCachedHomeSettings());
    void refreshHomeSettings();
    void loadPhotosAndAlbums();

    window.addEventListener('pageshow', handleRefresh);
    window.addEventListener('focus', handleRefresh);
    window.addEventListener(HOME_SETTINGS_EVENT, handleSettingsUpdated);
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      active = false;
      window.removeEventListener('pageshow', handleRefresh);
      window.removeEventListener('focus', handleRefresh);
      window.removeEventListener(HOME_SETTINGS_EVENT, handleSettingsUpdated);
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const settings = homeSettings || DEFAULT_HOME_SETTINGS;

  return (
    <div className="flex flex-col min-h-screen">
      <section className="relative w-full h-[420px] overflow-hidden">
        <img
          key={`${settings.heroImage}-${settings.updatedAt}`}
          src={settings.heroImage}
          alt="우리 가족"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/40"></div>

        <div className="relative z-10 flex flex-col justify-end h-full px-6 pb-8">
          <span className="text-background-50/90 text-xs font-medium tracking-widest uppercase mb-3">
            {settings.heroEyebrow}
          </span>
          <h1 className="text-background-50 text-3xl font-heading font-bold leading-tight mb-4">
            <span className="whitespace-pre-line">{settings.heroTitle}</span>
          </h1>
          <Link
            to="/upload"
            className="inline-flex items-center gap-2 bg-background-50/20 backdrop-blur-sm text-background-50 px-5 py-2.5 rounded-full text-sm font-medium border border-background-50/30 hover:bg-background-50/30 transition-all whitespace-nowrap w-fit cursor-pointer"
          >
            <i className="ri-add-line"></i>
            {settings.heroButtonText}
          </Link>
        </div>

        <div className="absolute bottom-6 right-6 z-10">
          <span className="text-background-50/40 text-[10px] tracking-[0.3em] uppercase font-medium">
            {settings.heroBrandText}
          </span>
        </div>
      </section>

      <section className="px-4 pt-5">
        <Link
          to="/search"
          className="flex items-center gap-3 rounded-2xl border border-background-200/80 bg-secondary-50 px-4 py-3.5 transition-colors hover:bg-secondary-100"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-background-50 text-accent-500 shadow-sm"><i className="ri-search-line text-lg" /></div>
          <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-foreground-800">사진 찾기</p><p className="mt-0.5 text-[11px] text-foreground-400">제목·파일명·앨범·촬영일로 한 번에 검색</p></div>
          <i className="ri-arrow-right-s-line text-xl text-foreground-400" />
        </Link>
        <Link
          to="/search?favoriteOnly=true"
          className="mt-2 flex items-center gap-3 rounded-2xl bg-red-50 px-4 py-3.5 transition-colors hover:bg-red-100"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-background-50 text-red-400 shadow-sm"><i className="ri-heart-3-fill text-lg" /></div>
          <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-foreground-800">내 즐겨찾기</p><p className="mt-0.5 text-[11px] text-foreground-400">나만 표시해 둔 사진 모아 보기</p></div>
          <i className="ri-arrow-right-s-line text-xl text-foreground-400" />
        </Link>
      </section>

      <section className="px-4 pt-8 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-xs text-accent-500 font-semibold tracking-wide uppercase">최근 업로드</span>
            <h2 className="text-lg font-heading font-bold text-foreground-950 mt-0.5">방금 올라온 사진</h2>
          </div>
          <Link to="/albums" className="text-sm text-foreground-500 hover:text-accent-500 transition-colors whitespace-nowrap cursor-pointer">
            전체보기 <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>

        {loading ? (
          <div className="flex gap-3 overflow-x-auto pb-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex-shrink-0 w-[140px]">
                <div className="w-[140px] h-[140px] rounded-xl bg-secondary-100 animate-pulse"></div>
                <div className="mt-2 h-3 w-20 bg-secondary-100 rounded animate-pulse"></div>
                <div className="mt-1 h-2.5 w-14 bg-secondary-100 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        ) : recentPhotos.length > 0 ? (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-none">
            {recentPhotos.map((photo) => (
              <Link key={photo.id} to={`/album/${photo.albumId}`} className="flex-shrink-0 w-[140px] group cursor-pointer">
                <div className="relative w-[140px] h-[140px] rounded-xl overflow-hidden bg-secondary-100">
                  <img src={photo.image} alt={photo.caption} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <div className="absolute top-2 left-2"><span className="bg-accent-500 text-background-50 text-[10px] font-bold px-2 py-0.5 rounded-full">NEW</span></div>
                </div>
                <p className="mt-2 text-xs font-medium text-foreground-800 truncate">{photo.caption}</p>
                <p className="text-[11px] text-foreground-500">{photo.shotDate}</p>
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center py-8"><p className="text-sm text-foreground-400">아직 올라온 사진이 없어요</p></div>
        )}
      </section>

      {memoryPhotos.length > 0 && (
        <section className="px-4 py-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-accent-500">작년 오늘</span>
              <h2 className="mt-0.5 text-lg font-heading font-bold text-foreground-950">{memoryDate}의 우리 가족</h2>
            </div>
            <Link to={`/slideshow?shotDate=${memoryDate.replaceAll('.', '-')}&source=search`} className="text-sm text-foreground-500 hover:text-accent-500">슬라이드쇼 <i className="ri-arrow-right-s-line" /></Link>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none">
            {memoryPhotos.map((photo) => (
              <Link key={photo.id} to={`/albums/${photo.albumId}/photos/${photo.id}`} className="w-[160px] flex-shrink-0">
                <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-secondary-100"><img src={photo.image} alt={photo.caption || '작년 오늘 사진'} className="h-full w-full object-cover" loading="lazy" /><span className="absolute left-2 top-2 rounded-full bg-black/35 px-2 py-1 text-[10px] font-semibold text-white backdrop-blur-sm">{photo.shotDate}</span></div>
                <p className="mt-2 truncate text-xs font-medium text-foreground-800">{photo.caption || photo.originalFilename}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="px-4 py-4">
        <div className="mb-4">
          <span className="text-xs text-accent-500 font-semibold tracking-wide uppercase">이번 달 인기 앨범</span>
          <h2 className="text-lg font-heading font-bold text-foreground-950 mt-0.5">모두가 보고 있는 추억</h2>
        </div>
        {popularAlbums.length > 0 && (
          <div className="flex gap-3">
            {popularAlbums.map((album, idx) => (
              <Link key={album.id} to={`/album/${album.id}`} className={`${idx === 0 ? 'w-[45%]' : 'w-[55%]'} group cursor-pointer`}>
                <div className={`relative ${idx === 0 ? 'aspect-[3/4]' : 'aspect-[16/10]'} rounded-xl overflow-hidden bg-secondary-100`}>
                  <img src={album.coverImage} alt={album.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-background-50 text-sm font-bold truncate">{album.title}</p>
                    <p className="text-background-50/70 text-[11px]">{album.photoCount}장의 사진</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="px-4 py-8 mt-2">
        <div className="bg-secondary-50 rounded-2xl p-6 text-center">
          <i className="ri-double-quotes-l text-2xl text-accent-300"></i>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed font-heading">가족이란 함께 웃고, 함께 울고,<br />함께 자라는 가장 따뜻한 울타리입니다</p>
          <p className="mt-3 text-xs text-foreground-400">— 우리 가족의 다짐</p>
        </div>
      </section>
    </div>
  );
}
